import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, PRIMARY_OUTLET, Router, RouterOutlet } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs/operators';

import { AlertsStore } from '../../../alerts/alerts.store';
import { CreateLinkRequestModalComponent } from '../../../link-requests/components/create-link-request-modal/create-link-request-modal.component';
import { LinkRequestsStore } from '../../../link-requests/link-requests.store';
import { MaintenancePeriodCardComponent } from '../../../maintenance-period/components/maintenance-period-card/maintenance-period-card.component';
import { MaintenancePeriodStore } from '../../../maintenance-period/maintenance-period.store';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { NavTab, NavTabComponent } from '../../../shared/components/nav-tab/nav-tab.component';
import { FavoriteListComponent } from '../../../site-favorite/components/favorite-list/favorite-list.component';
import { SiteListFacade } from '../../../site-list/facades/site-list.facade';
import { DashboardTabStore } from '../../dashboard-tab.store';
import { AlertIndicatorComponent } from '../alert-indicator/alert-indicator.component';

type DashboardPath = 'sites' | 'alerts' | 'link-requests';

type DashboardLink = {
    path: DashboardPath;
    label: string;
    icon: string;
};

const DASHBOARD_LINKS: DashboardLink[] = [
    { path: 'sites', label: 'ALL_INSTALLATIONS', icon: 'assets/img/tab-navigation/sites.svg' },
    { path: 'alerts', label: 'INSTALLATIONS_IN_ALERT', icon: 'assets/img/alert.svg' },
    { path: 'link-requests', label: 'PENDING_REQUESTS', icon: 'assets/img/tab-navigation/link-requests.svg' },
];

@Component({
    selector: 'app-dashboard',
    imports: [
        RouterOutlet,
        TranslatePipe,
        ButtonComponent,
        NavTabComponent,
        MaintenancePeriodCardComponent,
        FavoriteListComponent,
        AlertIndicatorComponent,
        CreateLinkRequestModalComponent,
    ],
    templateUrl: './dashboard.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './dashboard.component.scss',
})
export class DashboardComponent {
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly langChange = toSignal(this.translate.onLangChange);
    private readonly currentUrl = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => event.urlAfterRedirects),
        ),
        { initialValue: this.router.url },
    );
    private readonly linkRequests = inject(LinkRequestsStore);
    private readonly siteList = inject(SiteListFacade);
    private readonly alertsStore = inject(AlertsStore);
    private readonly activePath = computed(
        () => this.router.parseUrl(this.currentUrl()).root.children[PRIMARY_OUTLET]?.segments[0]?.path,
    );

    protected readonly linkRequestModalOpen = signal(false);
    protected readonly maintenancePeriod = inject(MaintenancePeriodStore).current;

    protected readonly tabs = computed<NavTab[]>(() => {
        this.langChange();
        const counts: Record<DashboardPath, number> = {
            sites: this.siteList.siteCount(),
            alerts: this.alertsStore.pendingCount(),
            'link-requests': this.linkRequests.count(),
        };

        return DASHBOARD_LINKS.map((link) => ({
            label: this.translate.instant(link.label),
            icon: link.icon,
            chip: counts[link.path],
            active: this.activePath() === link.path,
            routerLink: `/${link.path}`,
        }));
    });

    constructor() {
        this.linkRequests.load();
        const tabStore = inject(DashboardTabStore);
        effect(() => {
            const path = this.activePath();
            if (DASHBOARD_LINKS.some((link) => link.path === path)) tabStore.visit(`/${path}`);
        });
    }

    onLinkRequestClick(): void {
        this.linkRequestModalOpen.set(true);
    }
}
